// apps/provider/src/services/api/scheduling.js
// Provider availability, appointments, schedule overrides, appointment types,
// available slots, patient self-scheduling, and schedule summaries.
//
// Methods: createProviderAvailability, getProviderAvailability, getProviderAvailabilityPublic,
//          updateProviderAvailability, deleteProviderAvailability, getProvidersAvailability,
//          getAvailableSlots, checkSlotAvailability,
//          createAppointmentType, getAppointmentTypes, getAppointmentTypesPublic,
//          updateAppointmentType, deleteAppointmentType,
//          createScheduleOverride, getScheduleOverrides, getOrgScheduleOverrides,
//          updateScheduleOverride, deleteScheduleOverride,
//          quickScheduleAction,
//          createAppointment, getAppointments, getAppointmentsPublic, getAppointment,
//          updateAppointment, cancelAppointment, confirmAppointment,
//          markAppointmentNoShow, rescheduleAppointment,
//          requestAppointment, getPendingAppointmentRequests, approveAppointmentRequest,
//          declineAppointmentRequest,
//          getProviderScheduleSummary, getOrganizationScheduleSummary,
//          getUpcomingAppointments,
//          updateScheduleResponse, getSchedulingRequests, checkSlotsAvailability,
//          confirmSchedulingRequest, getSchedulingActivity
//
// Response types: @prism-ehr/shared → Appointment, AppointmentType, ProviderAvailability,
//                 ScheduleOverride, AvailableSlot, SlotAvailabilityCheck,
//                 SlotAvailabilityStatus, ScheduleConfirmationResult,
//                 ProviderScheduleSummary, OrganizationScheduleSummary,
//                 SchedulingRulesResolution, ProviderEligibility,
//                 BatchValidationResult, BatchCreateResult
import { retryOnBusy } from '../../utils/retryOnBusy';

/**
 * @returns {Object} Scheduling domain methods (bound to `this` of the composed apiService)
 */
export function createSchedulingMethods() {
  return {

    // ============ Provider Availability Management ============

    /**
     * Create a new provider availability rule.
     *
     * Xano: POST /provider_availability
     * Xano file: 738_provider_availability_POST.xs
     *
     * @param {Object} data - Availability data (day_of_week, start_time, end_time, etc.)
     * @returns {Promise<import('@prism-ehr/shared').ProviderAvailability>}
     */
    async createProviderAvailability(data) {
      return this.request('/provider_availability', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get availability rules for a single provider.
     *
     * Xano: GET /provider_availability
     * Xano file: 737_provider_availability_GET.xs
     *
     * @param {string} providerId
     * @param {{ locationId?: string }} [options] — when set, filters to that (provider, location) pair
     * @returns {Promise<import('@prism-ehr/shared').ProviderAvailability[]>}
     */
    async getProviderAvailability(providerId, { locationId } = {}) {
      const qp = new URLSearchParams({ provider_id: providerId });
      if (locationId) qp.append('location_id', locationId);
      return this.request(`/provider_availability?${qp.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get provider availability (public/unauthenticated).
     *
     * Xano: GET /public/provider-availability
     *
     * @param {string} providerId — comma-separated string supported
     * @param {{ locationId?: string }} [options] — when set, filters availability rows to that location
     * @returns {Promise<import('@prism-ehr/shared').ProviderAvailability[]>}
     */
    async getProviderAvailabilityPublic(providerId, { locationId, organizationId } = {}) {
      const qp = new URLSearchParams({ provider_id: providerId });
      if (organizationId) qp.append('organization_id', organizationId);
      if (locationId) qp.append('location_id', locationId);
      return this.publicRequest(`/public/provider-availability?${qp.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Update a provider availability rule.
     *
     * Xano: PATCH /provider_availability/{id}
     * Xano file: 739_provider_availability_provider_availability_id_PATCH.xs
     *
     * @param {string} id - Availability rule ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').ProviderAvailability>}
     */
    async updateProviderAvailability(id, data) {
      return this.request(`/provider_availability/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a provider availability rule.
     *
     * Xano: DELETE /provider_availability/{id}
     * Xano file: 735_provider_availability_provider_availability_id_DELETE.xs
     *
     * @param {string} id - Availability rule ID
     * @returns {Promise<void>}
     */
    async deleteProviderAvailability(id) {
      return this.request(`/provider_availability/${id}`, {
        method: 'DELETE'
      });
    },

    /**
     * Get availability for multiple providers with optional filters.
     *
     * Xano: GET /provider_availability
     * Xano file: 737_provider_availability_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string[]} [filters.provider_ids] - Provider IDs to filter
     * @param {string} [filters.location_id] - Location filter
     * @param {string} [filters.organization_id] - Organization filter
     * @param {boolean} [filters.is_active] - Active status filter
     * @returns {Promise<import('@prism-ehr/shared').ProviderAvailability[]>}
     */
    async getProvidersAvailability(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.provider_ids) queryParams.append('provider_ids', filters.provider_ids.join(','));
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);

      return this.request(`/provider_availability?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    // ============ Available Slots ============

    /**
     * Get available time slots for booking.
     *
     * Xano: GET /available_slot
     * Xano file: 752_available_slot_GET.xs
     *
     * @param {Object} query
     * @param {string} [query.provider_id] - Single provider
     * @param {string[]} [query.provider_ids] - Multiple providers
     * @param {string} [query.location_id] - Location filter
     * @param {string} query.start_date - Range start (YYYY-MM-DD)
     * @param {string} query.end_date - Range end (YYYY-MM-DD)
     * @param {string} [query.appointment_type] - Appointment type filter
     * @param {number} [query.appointment_duration_minutes] - Desired duration
     * @param {boolean} [query.include_flexible] - Include flexible slots
     * @returns {Promise<import('@prism-ehr/shared').AvailableSlot[]>}
     */
    async getAvailableSlots(query) {
      const queryParams = new URLSearchParams();

      if (query.provider_id) queryParams.append('provider_id', query.provider_id);
      if (query.provider_ids) queryParams.append('provider_ids', query.provider_ids.join(','));
      if (query.location_id) queryParams.append('location_id', query.location_id);
      queryParams.append('start_date', query.start_date);
      queryParams.append('end_date', query.end_date);
      if (query.appointment_type) queryParams.append('appointment_type', query.appointment_type);
      if (query.appointment_duration_minutes) queryParams.append('duration', query.appointment_duration_minutes);
      if (query.include_flexible !== undefined) queryParams.append('include_flexible', query.include_flexible);

      return this.request(`/available_slot?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Check if a specific time slot is available.
     * Used before booking or when rescheduling (pass appointment_id to exclude current).
     *
     * Xano: POST /check_slot_availability
     * Xano file: Not found in staging — may use available_slot logic
     *
     * @param {Object} data
     * @param {string} data.provider_id
     * @param {string} data.date - YYYY-MM-DD
     * @param {string} data.start_time - HH:mm
     * @param {string} data.end_time - HH:mm
     * @param {string} [data.appointment_id] - Exclude this appointment (for rescheduling)
     * @returns {Promise<import('@prism-ehr/shared').SlotAvailabilityCheck>}
     */
    async checkSlotAvailability(data) {
      return this.request('/check_slot_availability', {
        method: 'POST',
        body: JSON.stringify({
          provider_id: data.provider_id,
          date: data.date,
          start_time: data.start_time,
          end_time: data.end_time,
          appointment_id: data.appointment_id
        })
      });
    },

    // ============ Appointment Types ============

    /**
     * Create a new appointment type.
     *
     * Xano: POST /appointment_type
     * Xano file: 748_appointment_type_POST.xs
     *
     * @param {Object} data - Appointment type data (name, duration_minutes, color, etc.)
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType>}
     */
    async createAppointmentType(data) {
      return this.request('/appointment_type', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get all appointment types for an organization.
     *
     * Xano: GET /appointment_type
     * Xano file: 747_appointment_type_GET.xs
     *
     * @param {string} organizationId
     * @param {{ locationId?: string }} [options] — when set, filters to types valid at that location
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType[]>}
     */
    async getAppointmentTypes(organizationId, { locationId } = {}) {
      const qp = new URLSearchParams({ organization_id: organizationId });
      if (locationId) qp.append('location_id', locationId);
      return this.request(`/appointment_type?${qp.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get appointment types (public/unauthenticated).
     *
     * Xano: GET /appointment_types/public
     * Xano file: 896_appointment_types_public_GET.xs
     *
     * @param {string} organizationId
     * @param {{ locationId?: string }} [options] — when set, filters to types valid at that location
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType[]>}
     */
    async getAppointmentTypesPublic(organizationId, { locationId } = {}) {
      const qp = new URLSearchParams({ organization_id: organizationId, is_active: 'true' });
      if (locationId) qp.append('location_id', locationId);
      return this.publicRequest(`/appointment_types/public?${qp.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Update an appointment type.
     *
     * Xano: PATCH /appointment_type/{id}
     * Xano file: 749_appointment_type_appointment_type_id_PATCH.xs
     *
     * @param {string} id - Appointment type ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType>}
     */
    async updateAppointmentType(id, data) {
      return this.request(`/appointment_type/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete an appointment type.
     *
     * Xano: DELETE /appointment_type/{id}
     * Xano file: 745_appointment_type_appointment_type_id_DELETE.xs
     *
     * @param {string} id - Appointment type ID
     * @returns {Promise<void>}
     */
    async deleteAppointmentType(id) {
      return this.request(`/appointment_type/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Schedule Overrides ============

    /**
     * Create a schedule override (block time, add availability on a specific date).
     *
     * Xano: POST /schedule_override
     * Xano file: 743_schedule_override_POST.xs
     *
     * @param {Object} data - Override data (provider_id, date, is_available, etc.)
     * @returns {Promise<import('@prism-ehr/shared').ScheduleOverride>}
     */
    async createScheduleOverride(data) {
      return this.request('/schedule_override', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get schedule overrides for a provider within a date range.
     *
     * Xano: GET /schedule_override
     * Xano file: 742_schedule_override_GET.xs
     *
     * @param {string} providerId
     * @param {string} startDate - YYYY-MM-DD
     * @param {string} endDate - YYYY-MM-DD
     * @returns {Promise<import('@prism-ehr/shared').ScheduleOverride[]>}
     */
    async getScheduleOverrides(providerId, startDate, endDate) {
      const queryParams = new URLSearchParams({ provider_id: providerId });
      if (startDate) queryParams.set('start_date', startDate);
      if (endDate) queryParams.set('end_date', endDate);

      return this.request(`/schedule_override?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get all schedule overrides for the organization within a date range.
     * Omits provider_id so the backend returns ALL org overrides.
     *
     * Xano: GET /schedule_override (without provider_id filter)
     * Xano file: 742_schedule_override_GET.xs
     *
     * @param {string} startDate - YYYY-MM-DD
     * @param {string} endDate - YYYY-MM-DD
     * @returns {Promise<import('@prism-ehr/shared').ScheduleOverride[]>}
     */
    async getOrgScheduleOverrides(startDate, endDate) {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.set('start_date', startDate);
      if (endDate) queryParams.set('end_date', endDate);
      return this.request(`/schedule_override?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Update a schedule override.
     *
     * Xano: PATCH /schedule_override/{id}
     * Xano file: 744_schedule_override_schedule_override_id_PATCH.xs
     *
     * @param {string} id - Override ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').ScheduleOverride>}
     */
    async updateScheduleOverride(id, data) {
      return this.request(`/schedule_override/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a schedule override.
     *
     * Xano: DELETE /schedule_override/{id}
     * Xano file: 740_schedule_override_schedule_override_id_DELETE.xs
     *
     * @param {string} id - Override ID
     * @returns {Promise<void>}
     */
    async deleteScheduleOverride(id) {
      return this.request(`/schedule_override/${id}`, {
        method: 'DELETE'
      });
    },

    /**
     * Delete multiple schedule overrides in one request (org-scoped server-side).
     *
     * Xano: POST /schedule-overrides-batch-delete
     * Xano file: schedule-overrides-batch-delete_POST.xs
     *
     * @param {number[]} ids - Override IDs to delete
     * @returns {Promise<{deleted: number}>}
     */
    async batchDeleteScheduleOverrides(ids) {
      return this.request('/schedule-overrides-batch-delete', {
        method: 'POST',
        timeoutMs: 120000,
        body: JSON.stringify({ ids })
      });
    },

    /**
     * Create multiple schedule overrides for one provider in a single request
     * (org-stamped server-side). Emits ONE realtime event for the whole batch.
     *
     * Xano: POST /schedule-overrides-batch-create
     * Xano file: schedule-overrides-batch-create_POST.xs
     *
     * @param {string} providerId
     * @param {Array<Object>} overrides - Each: {date, type, blocks, reason, series_id, recurrence}
     * @returns {Promise<import('@prism-ehr/shared').ScheduleOverride[]>}
     */
    async batchCreateScheduleOverrides(providerId, overrides) {
      return this.request('/schedule-overrides-batch-create', {
        method: 'POST',
        timeoutMs: 120000,
        body: JSON.stringify({ provider_id: providerId, overrides })
      });
    },

    /**
     * Get schedule overrides (public/unauthenticated).
     * Supports comma-separated provider IDs.
     *
     * Xano: GET /public/schedule-overrides
     *
     * @param {string} providerIds - Single ID or comma-separated IDs
     * @param {string} [startDate] - YYYY-MM-DD
     * @param {string} [endDate] - YYYY-MM-DD
     * @returns {Promise<import('@prism-ehr/shared').ScheduleOverride[]>}
     */
    async getScheduleOverridesPublic(providerIds, startDate, endDate, { organizationId } = {}) {
      const params = new URLSearchParams({ provider_id: providerIds });
      if (organizationId) params.set('organization_id', organizationId);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);

      return this.publicRequest(`/public/schedule-overrides?${params.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Execute a quick schedule action (e.g., block rest of day, copy last week).
     *
     * Xano: POST /schedule_quick_action
     * Xano file: Not found in staging
     *
     * @param {import('@prism-ehr/shared').QuickScheduleAction} action
     * @returns {Promise<Object>}
     */
    async quickScheduleAction(action) {
      return this.request('/schedule_quick_action', {
        method: 'POST',
        body: JSON.stringify(action)
      });
    },

    // ============ Appointments ============

    /**
     * Create a new appointment.
     *
     * Xano: POST /appointment
     * Xano file: 646_appointment_POST.xs
     *
     * @param {Object} appointmentData
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async createAppointment(appointmentData) {
      return this.request('/appointment', {
        method: 'POST',
        body: JSON.stringify(appointmentData)
      });
    },

    /**
     * Get appointments with optional filters.
     *
     * Xano: GET /appointment
     * Xano file: 645_appointment_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.location_id]
     * @param {string} [filters.start_date] - YYYY-MM-DD
     * @param {string} [filters.start_after] - ISO timestamp
     * @param {string} [filters.end_date] - YYYY-MM-DD
     * @param {string} [filters.status] - Comma-separated statuses
     * @param {string} [filters.appointment_type]
     * @param {boolean} [filters.is_telehealth]
     * @returns {Promise<import('@prism-ehr/shared').Appointment[]>}
     */
    async getAppointments(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.provider_ids) queryParams.append('provider_ids', filters.provider_ids.join(','));
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.start_after) queryParams.append('start_after', filters.start_after);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.appointment_type) queryParams.append('appointment_type', filters.appointment_type);
      if (filters.is_telehealth !== undefined) queryParams.append('is_telehealth', filters.is_telehealth);
      if (filters.case_id) queryParams.append('case_id', filters.case_id);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      return this.request(`/appointment?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get appointments for the calendar via the normalized lightweight endpoint.
     *
     * The backend returns flat lists ({appointments, patients, cases,
     * referring_providers, users, appointment_types}) with each related record
     * included once, instead of per-row addons. This method stitches them back
     * into the standard nested appointment shape, so consumers receive the same
     * structure as getAppointments() minus the unused `encounter` object.
     *
     * Organization scoping is derived from the auth token server-side.
     *
     * Xano: GET /calendar-appointments
     * Xano file: calendar-appointments_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] - YYYY-MM-DD
     * @param {string} [filters.end_date] - YYYY-MM-DD
     * @param {string} [filters.location_id]
     * @param {string} [filters.status] - Comma-separated statuses
     * @param {string} [filters.appointment_type]
     * @param {string} [filters.patient_id]
     * @param {number} [filters.case_id]
     * @param {string} [filters.updated_after] - ISO timestamp; only appointments updated since
     * @param {string} [filters.appointment_id] - fetch a single stitched appointment (realtime ingest / post-create hydration)
     * @returns {Promise<import('@prism-ehr/shared').CalendarAppointment[]>}
     */
    async getCalendarAppointments(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();

      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.appointment_type) queryParams.append('appointment_type', filters.appointment_type);
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.case_id) queryParams.append('case_id', filters.case_id);
      if (filters.updated_after) queryParams.append('updated_after', filters.updated_after);
      if (filters.appointment_id) queryParams.append('appointment_id', filters.appointment_id);

      const data = await this.request(`/calendar-appointments?${queryParams.toString()}`, {
        method: 'GET',
        signal: options.signal
      });

      const byId = (list) => {
        const map = new Map();
        (Array.isArray(list) ? list : []).forEach(item => {
          if (item?.id != null) map.set(item.id, item);
        });
        return map;
      };

      // JSON columns from db.direct_query arrive as strings — parse defensively
      const parseJsonColumn = (val) => {
        if (typeof val !== 'string') return val;
        try { return JSON.parse(val); } catch { return null; }
      };

      const patients = byId(
        (Array.isArray(data?.patients) ? data.patients : []).map(p => ({
          ...p,
          insurance: parseJsonColumn(p.insurance),
        }))
      );
      const referringProviders = byId(data?.referring_providers);
      const users = byId(data?.users);
      const appointmentTypes = byId(data?.appointment_types);

      const cases = new Map();
      (Array.isArray(data?.cases) ? data.cases : []).forEach(c => {
        if (c?.id == null) return;
        const referring_provider = c.referring_provider_id != null
          ? referringProviders.get(c.referring_provider_id) || null
          : null;
        cases.set(c.id, referring_provider ? { ...c, referring_provider } : c);
      });

      return (Array.isArray(data?.appointments) ? data.appointments : []).map(apt => ({
        ...apt,
        // list columns are JSON in Xano; a direct_query feed returns them as strings
        tags: parseJsonColumn(apt.tags),
        equipment_needed: parseJsonColumn(apt.equipment_needed),
        patient: apt.patient_id != null ? patients.get(apt.patient_id) || null : null,
        case: apt.case_id != null ? cases.get(apt.case_id) || null : null,
        provider: apt.provider_id != null ? users.get(apt.provider_id) || null : null,
        _appointment_type: apt.appointment_type_id != null ? appointmentTypes.get(apt.appointment_type_id) || null : null,
        _checked_in_by_user: apt.checked_in_by != null ? users.get(apt.checked_in_by) || null : null,
      }));
    },

    /**
     * Get appointments (public/unauthenticated) for a provider within a date range.
     *
     * Xano: GET /appointments/public
     * Xano file: 810_appointments_public_GET.xs
     *
     * @param {Object} data
     * @param {string} data.provider_id
     * @param {Object} data.date_range
     * @param {string} data.date_range.start - ISO date
     * @param {string} data.date_range.end - ISO date
     * @param {string} [data.location_id] - when set, filters to appointments at this location
     * @returns {Promise<import('@prism-ehr/shared').Appointment[]>}
     */
    async getAppointmentsPublic(data) {
      const qp = new URLSearchParams({
        provider_id: data.provider_id,
        organization_id: data.organization_id,
        start_date: data.date_range.start,
        end_date: data.date_range.end
      });
      if (data.location_id) qp.append('location_id', data.location_id);
      return this.publicRequest(`/appointments/public?${qp.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single appointment by ID.
     *
     * Xano: GET /appointment/{id}
     * Xano file: 644_appointment_appointment_id_GET.xs
     *
     * @param {string} id - Appointment ID
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async getAppointment(id) {
      return this.request(`/appointment/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Update an appointment.
     *
     * Xano: PATCH /appointment/{id}
     * Xano file: 647_appointment_appointment_id_PATCH.xs
     *
     * @param {string} id - Appointment ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async updateAppointment(id, data) {
      return this.request(`/appointment/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Permanently delete an appointment.
     *
     * Xano: DELETE /appointment/{id}
     * Xano file: 356_appointment_appointment_id_DELETE.xs
     *
     * @param {string} id - Appointment ID
     * @param {string} [reason] - Stamped into the audit DELETE snapshot
     * @param {{ discardEncounter?: boolean, reassignEncounterTo?: string }} [opts]
     *   discardEncounter: also delete the linked encounter + unsigned notes (signed notes
     *   are unlinked and kept; attached billing / clinical records block). Xano function:
     *   encounter_discard. reassignEncounterTo: move the linked encounter + unsigned notes
     *   to this future appointment instead (Xano function: encounter_reassign); wins over
     *   discardEncounter.
     * @returns {Promise<void>}
     */
    async deleteAppointment(id, reason, { discardEncounter = false, reassignEncounterTo = null } = {}) {
      const params = new URLSearchParams();
      if (reason) params.set('delete_reason', reason);
      // Opt-in: move the linked encounter to a future appointment, or discard it
      // (signed notes are unlinked and kept). Server-side guards in Xano
      // `encounter_reassign` / `encounter_discard`.
      if (reassignEncounterTo) params.set('reassign_encounter_to', reassignEncounterTo);
      else if (discardEncounter) params.set('discard_encounter', 'true');
      const query = params.toString();
      return this.request(`/appointment/${id}${query ? `?${query}` : ''}`, {
        method: 'DELETE'
      });
    },

    /**
     * List deleted appointments for a patient (read from the audit trail —
     * old_data snapshots carry deleted_by/deleted_reason).
     *
     * Xano: GET /patient-deleted-appointments
     * Xano file: patient-deleted-appointments_GET.xs
     *
     * @param {string} patientId
     * @returns {Promise<Array>}
     */
    async getDeletedAppointments(patientId) {
      return this.request(`/patient-deleted-appointments?patient_id=${patientId}`, {
        method: 'GET'
      });
    },

    /**
     * Cancel an appointment with a reason.
     *
     * Xano: PATCH /appointment/{id}
     * Xano file: 358_appointment_appointment_id_PATCH.xs (staging 647)
     *
     * @param {string} id - Appointment ID
     * @param {string} reason - Cancellation reason
     * @param {string} [cancelledBy] - User id
     * @param {{ discardEncounter?: boolean, reassignEncounterTo?: string }} [opts]
     *   discardEncounter: delete the linked encounter + unsigned notes; the PATCH fails
     *   (400) when a signed note or attached records exist. Xano function: encounter_discard.
     *   reassignEncounterTo: move the linked encounter + unsigned notes to this future
     *   appointment instead (Xano function: encounter_reassign); wins over discardEncounter.
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async cancelAppointment(id, reason, cancelledBy, { discardEncounter = false, reassignEncounterTo = null } = {}) {
      return this.updateAppointment(id, {
        status: 'cancelled',
        cancellation_reason: reason,
        cancelled_at: Date.now(),
        cancelled_by: cancelledBy || null,
        // Opt-in: move the linked encounter to a future appointment, or discard it
        // + its unsigned notes (blocked server-side when a signed note or attached
        // records exist). Both are read from raw input by Xano, never written to the row.
        ...(reassignEncounterTo
          ? { reassign_encounter_to: reassignEncounterTo }
          : (discardEncounter && { discard_encounter: true }))
      });
    },

    /**
     * Undo a cancellation, restoring the appointment to scheduled.
     *
     * Xano: PATCH /appointment/{id}
     * Xano file: 647_appointment_appointment_id_PATCH.xs
     *
     * @param {string} id - Appointment ID
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async undoAppointmentCancel(id) {
      return this.updateAppointment(id, {
        status: 'scheduled',
        cancelled_at: null,
        cancellation_reason: null,
        cancelled_by: null
      });
    },

    /**
     * Undo a no-show, restoring the appointment to scheduled.
     *
     * Xano: PATCH /appointment/{id}
     * Xano file: 647_appointment_appointment_id_PATCH.xs
     *
     * @param {string} id - Appointment ID
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async undoAppointmentNoShow(id) {
      return this.updateAppointment(id, {
        status: 'scheduled',
        no_show_at: null
      });
    },

    /**
     * Undo mark-complete, restoring the appointment to scheduled.
     *
     * Xano: PATCH /appointment/{id}
     * Xano file: 647_appointment_appointment_id_PATCH.xs
     *
     * @param {string} id - Appointment ID
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async undoAppointmentComplete(id) {
      return this.updateAppointment(id, {
        status: 'scheduled',
        completed_at: null
      });
    },

    /**
     * Confirm an appointment.
     *
     * Xano: POST /appointment/{id}/confirm
     * Xano file: Not found in staging — likely handled via PATCH 647
     *
     * @param {string} id - Appointment ID
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async confirmAppointment(id) {
      return this.request(`/appointment/${id}/confirm`, {
        method: 'POST',
        body: JSON.stringify({
          confirmed_at: Date.now()
        })
      });
    },

    /**
     * Mark an appointment as no-show.
     *
     * Xano: PATCH /appointment/{id}
     * Xano file: 358_appointment_appointment_id_PATCH.xs (staging 647)
     *
     * @param {string} id - Appointment ID
     * @param {{ discardEncounter?: boolean, reassignEncounterTo?: string }} [opts] - see cancelAppointment
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async markAppointmentNoShow(id, { discardEncounter = false, reassignEncounterTo = null } = {}) {
      return this.updateAppointment(id, {
        status: 'no_show',
        no_show_at: Date.now(),
        // Opt-in encounter reassign / discard; see cancelAppointment.
        ...(reassignEncounterTo
          ? { reassign_encounter_to: reassignEncounterTo }
          : (discardEncounter && { discard_encounter: true }))
      });
    },

    /**
     * Reschedule an appointment to a new date/time.
     *
     * Xano: POST /appointment/{id}/reschedule
     * Xano file: Not found in staging — likely handled via PATCH 647
     *
     * @param {string} id - Appointment ID
     * @param {Object} newSchedule
     * @param {string} newSchedule.date - YYYY-MM-DD
     * @param {string} newSchedule.start_time - HH:mm
     * @param {string} newSchedule.end_time - HH:mm
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async rescheduleAppointment(id, newSchedule) {
      return this.request(`/appointment/${id}/reschedule`, {
        method: 'POST',
        body: JSON.stringify({
          appointment_date: newSchedule.date,
          start_time: newSchedule.start_time,
          end_time: newSchedule.end_time,
          rescheduled_at: Date.now()
        })
      });
    },

    // ============ Patient Self-Scheduling ============

    /**
     * Request an appointment (approval-based scheduling).
     *
     * Xano: POST /appointment_request
     * Xano file: Not found in staging
     *
     * @param {Object} data - Appointment request data
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async requestAppointment(data) {
      return this.request('/appointment_request', {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          status: 'pending_approval',
          requested_at: Date.now()
        })
      });
    },

    /**
     * Get pending appointment requests for a provider.
     *
     * Xano: GET /appointment (filtered by status=pending_approval)
     * Xano file: 645_appointment_GET.xs
     *
     * @param {string} providerId
     * @returns {Promise<import('@prism-ehr/shared').Appointment[]>}
     */
    async getPendingAppointmentRequests(providerId) {
      return this.request(`/appointment?provider_id=${providerId}&status=pending_approval`, {
        method: 'GET'
      });
    },

    /**
     * Approve a pending appointment request.
     *
     * Xano: POST /appointment/{appointmentId}/approve
     * Xano file: Not found in staging — likely handled via PATCH 647
     *
     * @param {string} appointmentId
     * @param {Object} [adjustments={}] - Optional time adjustments
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async approveAppointmentRequest(appointmentId, adjustments = {}) {
      return this.request(`/appointment/${appointmentId}/approve`, {
        method: 'POST',
        body: JSON.stringify({
          status: 'confirmed',
          approved_at: Date.now(),
          ...adjustments
        })
      });
    },

    /**
     * Decline a pending appointment request.
     *
     * Xano: POST /appointment/{appointmentId}/decline
     * Xano file: Not found in staging — likely handled via PATCH 647
     *
     * @param {string} appointmentId
     * @param {string} reason - Decline reason
     * @returns {Promise<import('@prism-ehr/shared').Appointment>}
     */
    async declineAppointmentRequest(appointmentId, reason) {
      return this.request(`/appointment/${appointmentId}/decline`, {
        method: 'POST',
        body: JSON.stringify({
          status: 'declined',
          declined_at: Date.now(),
          decline_reason: reason
        })
      });
    },

    // ============ Provider Schedule Summary ============

    /**
     * Get a schedule summary for a single provider on a specific date.
     *
     * Xano: GET /provider_schedule_summary
     * Xano file: Not found in staging
     *
     * @param {string} providerId
     * @param {string} date - YYYY-MM-DD
     * @returns {Promise<import('@prism-ehr/shared').ProviderScheduleSummary>}
     */
    async getProviderScheduleSummary(providerId, date) {
      return this.request(`/provider_schedule_summary?provider_id=${providerId}&date=${date}`, {
        method: 'GET'
      });
    },

    /**
     * Get a schedule summary for an entire organization on a specific date.
     *
     * Xano: GET /organization_schedule_summary
     * Xano file: Not found in staging
     *
     * @param {string} organizationId
     * @param {string} date - YYYY-MM-DD
     * @returns {Promise<import('@prism-ehr/shared').OrganizationScheduleSummary>}
     */
    async getOrganizationScheduleSummary(organizationId, date) {
      return this.request(`/organization_schedule_summary?organization_id=${organizationId}&date=${date}`, {
        method: 'GET'
      });
    },

    /**
     * Get upcoming appointments for a patient (sorted by start time ascending).
     *
     * Xano: GET /appointment (filtered by patient_id, start_after, status)
     * Xano file: 645_appointment_GET.xs
     *
     * Every scheduled/confirmed appointment for the patient that starts after
     * now. GET /appointment has no limit or sort inputs, so the list is neither
     * capped nor ordered; callers sort by start_timestamp.
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').Appointment[]>}
     */
    async getUpcomingAppointments(patientId) {
      const now = new Date().toISOString();
      return this.request(
        `/appointment?patient_id=${patientId}&start_after=${now}&status=scheduled,confirmed`,
        { method: 'GET' }
      );
    },

    // ============ Scheduling Rules & Batch Creation ============

    /**
     * Resolve scheduling rules for an org/location (location overrides org).
     *
     * Xano: GET /scheduling-rules/resolve
     *
     * @param {string} orgId - Organization ID
     * @param {string} [locationId] - Optional location ID for overrides
     * @returns {Promise<import('@prism-ehr/shared').SchedulingRulesResolution>}
     */
    async getSchedulingRules(orgId, locationId) {
      let endpoint = `/scheduling-rules/resolve?organization_id=${orgId}`;
      if (locationId) endpoint += `&location_id=${locationId}`;
      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * @deprecated Eligibility is computed client-side by
     * `evaluateProviderEligibility()` (shared availabilityEngine) from the rows
     * returned by getSchedulingDataBatch(). No callers remain; the endpoint is
     * left in Xano pending removal.
     *
     * Xano: POST /scheduling/provider-eligibility
     *
     * @param {Object} data
     * @param {string} data.organization_id
     * @param {string} [data.location_id]
     * @param {import('@prism-ehr/shared').SchedulingRule} data.scheduling_rule
     * @param {string} data.start_date - YYYY-MM-DD
     * @param {string[]} [data.provider_ids]
     * @returns {Promise<import('@prism-ehr/shared').ProviderEligibility[]>}
     */
    async checkProviderEligibility(data) {
      return this.request('/scheduling/provider-eligibility', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Raw scheduling rows for several providers over one window, for
     * client-side eligibility (the engine runs per provider on these rows).
     *
     * Xano: POST /scheduling/suggest-slots-batch
     *
     * @param {Object} data
     * @param {string} data.provider_ids - comma-separated provider ids (never empty)
     * @param {string} data.start_date - YYYY-MM-DD
     * @param {number} data.weeks_out
     * @param {string} [data.location_id]
     * @returns {Promise<import('@prism-ehr/shared').SchedulingDataBatchResponse>}
     */
    async getSchedulingDataBatch(data) {
      return this.request('/scheduling/suggest-slots-batch', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get raw scheduling data (availability, existing appointments) for slot suggestion.
     * Slot generation algorithm runs client-side.
     *
     * Xano: POST /scheduling/suggest-slots
     *
     * @param {import('@prism-ehr/shared').SlotSuggestionRequest} data
     * @returns {Promise<Object>} Raw provider availability and appointment data
     */
    async getSchedulingData(data) {
      return this.request('/scheduling/suggest-slots', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Validate a batch of appointment slots before creation.
     *
     * Xano: POST /scheduling/batch-validate
     *
     * @param {import('@prism-ehr/shared').BatchAppointmentRequest} data
     * @returns {Promise<import('@prism-ehr/shared').BatchValidationResult>}
     */
    async validateBatchAppointments(data) {
      return this.request('/scheduling/batch-validate', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Create a batch of appointments (validates first).
     *
     * Xano: POST /scheduling/batch-create
     *
     * @param {import('@prism-ehr/shared').BatchAppointmentRequest} data
     * @returns {Promise<import('@prism-ehr/shared').BatchCreateResult>}
     */
    async createBatchAppointments(data) {
      return this.request('/scheduling/batch-create', {
        method: 'POST',
        timeoutMs: 120000,
        body: JSON.stringify(data)
      });
    },

    // ============ Form-Based Scheduling ============

    /**
     * Update a scheduling-related form response (reschedule, reassign provider, etc.).
     *
     * Xano: PATCH /form-responses/{responseId}/scheduling-update
     * Xano file: 730_form_responses_form_responses_id_scheduling_update_PATCH.xs
     *
     * @param {string} responseId - Form response ID
     * @param {Object} responseData - Updated scheduling data
     * @param {boolean} providerChanged - Whether provider was reassigned
     * @param {boolean} notifyProvider - Send notification to provider
     * @param {boolean} markScheduled - Mark the response as scheduled
     * @returns {Promise<Object>}
     */
    async updateScheduleResponse(responseId, responseData, providerChanged, notifyProvider, markScheduled) {
      return this.request(`/form-responses/${responseId}/scheduling-update`, {
        method: 'PATCH',
        body: JSON.stringify({
          provider_changed: providerChanged,
          notify_provider: notifyProvider,
          mark_scheduled: markScheduled,
          ...responseData
        })
      });
    },

    /**
     * Get pending scheduling requests (form responses with type='scheduling' and scheduled=false).
     * Used for require_approval scheduling mode to review patient time preferences.
     *
     * Xano: GET /form_responses/scheduling-requests
     * Xano file: 1093_form_responses_scheduling_requests_GET.xs
     *
     * @param {Object} [options={}]
     * @param {string} [options.organizationId] - Filter by organization
     * @param {string} [options.locationId] - Filter by location
     * @param {string} [options.providerId] - Filter by provider in preferred slots
     * @param {number} [options.page=1] - Page number
     * @param {number} [options.perPage=25] - Items per page
     * @returns {Promise<{items: Array, cursorNext: number|null, itemsReceived: number}>}
     */
    async getSchedulingRequests({ organizationId = null, locationId = null, providerId = null, page = 1, perPage = 25 } = {}) {
      let endpoint = `/form_responses/scheduling-requests?page=${page}&per_page=${perPage}`;

      if (organizationId) endpoint += `&organization_id=${organizationId}`;
      if (locationId) endpoint += `&location_id=${locationId}`;
      if (providerId) endpoint += `&provider_id=${providerId}`;

      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * Check availability status for preferred time slots.
     * Returns which slots are still available, booked, or have other pending requests.
     *
     * Xano: POST /form-responses/check-slots-availability
     * Xano file: 1094_form_responses_check_slots_availability_POST.xs
     *
     * @param {import('@prism-ehr/shared').PreferredSlot[]} slots
     * @returns {Promise<import('@prism-ehr/shared').SlotAvailabilityStatus[]>}
     */
    async checkSlotsAvailability(slots) {
      return this.request('/form-responses/check-slots-availability', {
        method: 'POST',
        body: JSON.stringify({ slots })
      });
    },

    /**
     * Scheduling > Activity page: practice-wide appointment activity (staff +
     * online reschedules, cancellations, no-shows, restores, waitlist joins and
     * claims) plus waitlist auto-offer activity. Tier 3: three org-bounded
     * indexed reads. Server-side scope from the scheduling permissions
     * (scheduling_view_activity required; rows clamped without
     * scheduling_view_others / scheduling_view_all_locations).
     *
     * Xano: GET /scheduling/activity
     * Xano file: scheduling_activity_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD (default: 30 days ago)
     * @param {string} [filters.end_date] — YYYY-MM-DD (default: today)
     * @param {string} [filters.provider_id]
     * @param {string} [filters.location_id]
     * @param {number} [filters.page]
     * @param {number} [filters.per_page] — max 100
     * @param {string} [filters.channel] — "staff" | "online" (link + portal); applies to the list and the summary
     * @param {string} [filters.action] — one log action key; applies to the list only
     * @param {boolean} [filters.list_only] — skip the summary + waitlist reads (page flips, card clicks)
     * @param {{ signal?: AbortSignal }} [options={}]
     * @returns {Promise<import('@prism-ehr/shared').SchedulingActivityResponse>}
     */
    getSchedulingActivity(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.page) queryParams.append('page', String(filters.page));
      if (filters.per_page) queryParams.append('per_page', String(filters.per_page));
      if (filters.channel) queryParams.append('channel', filters.channel);
      if (filters.action) queryParams.append('action', filters.action);
      if (filters.list_only) queryParams.append('list_only', 'true');

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/scheduling/activity${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 30000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Confirm a scheduling request — creates the appointment from a preferred slot.
     *
     * Xano: POST /form-responses/{responseId}/confirm-scheduling
     * Xano file: 1095_form_responses_form_responses_id_confirm_scheduling_POST.xs
     *
     * @param {string} responseId - Form response ID
     * @param {Object} data
     * @param {import('@prism-ehr/shared').PreferredSlot} data.selectedSlot - The slot to confirm
     * @param {boolean} [data.sendConfirmation=true] - Send confirmation to patient
     * @param {boolean} [data.sendEmail=true] - Send email notification
     * @param {boolean} [data.sendSms=false] - Send SMS notification
     * @param {string} [data.intakeFormId] - Optional intake form to send
     * @returns {Promise<import('@prism-ehr/shared').ScheduleConfirmationResult>}
     */
    async confirmSchedulingRequest(responseId, { selectedSlot, sendConfirmation = true, sendEmail = true, sendSms = false, intakeFormIds = [] }) {
      return this.request(`/form-responses/${responseId}/confirm-scheduling`, {
        method: 'POST',
        body: JSON.stringify({
          selected_slot: selectedSlot,
          send_confirmation: sendConfirmation,
          send_email: sendEmail,
          send_sms: sendSms,
          intake_form_ids: intakeFormIds,
          intake_form_id: intakeFormIds?.[0] || null,
          confirmed_at: Date.now()
        })
      });
    },
  };
}
